import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { STORAGE_KEYS } from '../../constants/app';
import { apiCall } from '../../utils/apiClient';
import { logout } from './authSlice';

const loadWishlistFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.WISHLIST);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveWishlistToStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEYS.WISHLIST, JSON.stringify(items));
  } catch (error) {
    console.error('Failed to save wishlist:', error);
  }
};

// Slice defined first so thunks below can reference its sync actions
const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState: {
    items: loadWishlistFromStorage(),
    loading: false,
    error: null,
  },
  reducers: {
    clearWishlist(state) {
      state.items = [];
      saveWishlistToStorage([]);
    },
    setWishlist(state, action) {
      state.items = Array.isArray(action.payload) ? action.payload : [];
      saveWishlistToStorage(state.items);
    },
    // Optimistic local reducers (used directly by thunks for instant UI)
    addItemLocal(state, action) {
      const product = action.payload;
      const alreadyIn = state.items.some(
        (i) => String(i.id) === String(product.id)
      );
      if (!alreadyIn) {
        state.items.push(product);
        saveWishlistToStorage(state.items);
      }
    },
    removeItemLocal(state, action) {
      const id = String(action.payload);
      state.items = state.items.filter((i) => String(i.id) !== id);
      saveWishlistToStorage(state.items);
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchWishlist — use string types to avoid circular reference with thunks
      .addCase('wishlist/fetch/pending', (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase('wishlist/fetch/fulfilled', (state, action) => {
        state.loading = false;
        state.items = Array.isArray(action.payload) ? action.payload : [];
        saveWishlistToStorage(state.items);
      })
      .addCase('wishlist/fetch/rejected', (state) => {
        state.loading = false;
        // Keep existing local items on fetch failure
      })
      // addToWishlist — optimistic update already applied; sync state when API responds
      .addCase('wishlist/add/fulfilled', (state, action) => {
        if (Array.isArray(action.payload)) {
          state.items = action.payload;
          saveWishlistToStorage(state.items);
        }
      })
      .addCase('wishlist/add/rejected', (state) => {
        // Local optimistic update already applied, keep it
        state.error = 'Could not sync wishlist with server';
      })
      // removeFromWishlist — same pattern
      .addCase('wishlist/remove/fulfilled', (state, action) => {
        if (Array.isArray(action.payload)) {
          state.items = action.payload;
          saveWishlistToStorage(state.items);
        }
      })
      .addCase('wishlist/remove/rejected', (state) => {
        state.error = 'Could not sync wishlist with server';
      })
      .addCase(logout, (state) => {
        state.items = [];
        state.error = null;
        saveWishlistToStorage([]);
      });
  },
});

export const { clearWishlist, setWishlist, addItemLocal, removeItemLocal } =
  wishlistSlice.actions;

// Thunks defined after slice — dispatch optimistic sync action first, then call API
export const fetchWishlist = createAsyncThunk('wishlist/fetch', async () => {
  return await apiCall({ method: 'GET', url: '/wishlist' });
});

export const addToWishlist = createAsyncThunk(
  'wishlist/add',
  async (product, { dispatch }) => {
    dispatch(addItemLocal(product)); // instant UI update
    return await apiCall({ method: 'POST', url: '/wishlist', data: { product } });
  }
);

export const removeFromWishlist = createAsyncThunk(
  'wishlist/remove',
  async (productId, { dispatch }) => {
    dispatch(removeItemLocal(productId)); // instant UI update
    return await apiCall({
      method: 'DELETE',
      url: `/wishlist/${encodeURIComponent(productId)}`,
    });
  }
);

export default wishlistSlice.reducer;
